<script setup lang="ts">
import {computed, ref, watch} from 'vue';
import {ElButton, ElButtonGroup} from 'element-plus';
import {DashboardReeksEnum, DashboardReeksEnum_name, ReeksenPage, ReeksNotitie, ReeksSerie, TOEGANKELIJKHEID_KEUZE, TOEGANKELIJKHEID_REEKSEN} from 'common';
import {dashboardReeksen} from '@/base/callBackend.js';
import {dashboardOrgs, dashboardPeriod, laadDashboard} from '@/base/dashboardState.js';
import {localStorageGetJson, localStorageSetJson} from '@/base/utils.js';
import ReeksChart from '@/views/dashboard/ReeksChart.vue';
import ReeksNotities from '@/views/dashboard/ReeksNotities.vue';

const STORAGE_KEUZE_ID: string = 'dashboard:toegankelijkheid:keuze';
const UITLEG: string           = 'Risico op vertraging geldt indien de waarde 90-99% van de nulmeting is, onder de 90% geldt off-track.';

const loading  = ref<boolean>(true);
const page     = ref<ReeksenPage>({series: [], zzc: [], notities: []});
const keuze    = ref<DashboardReeksEnum>(TOEGANKELIJKHEID_KEUZE.includes(localStorageGetJson(STORAGE_KEUZE_ID)) ? localStorageGetJson(STORAGE_KEUZE_ID) : DashboardReeksEnum.adherentie);
let loadSeq: number = 0;

const eenGroep    = computed<boolean>(() => dashboardOrgs.value.length === 1);
const wachttijden = computed<ReeksSerie[]>(() => page.value.series.filter(s => s.reeks === DashboardReeksEnum.wachttijdenTreeknorm));
// assumption B: without an organisation choice every group's series is shown, plus the ZZC totaal series
const gekozen     = computed<ReeksSerie[]>(() => {
    const vanGroepen: ReeksSerie[] = page.value.series.filter(s => s.reeks === keuze.value);
    const vanZzc: ReeksSerie[]     = dashboardOrgs.value.length === 0 ? page.value.zzc.filter(s => s.reeks === keuze.value) : [];
    return [...vanGroepen, ...vanZzc];
});
const notities    = computed<ReeksNotitie[]>(() => page.value.notities);

watch(keuze, (k: DashboardReeksEnum) => localStorageSetJson(STORAGE_KEUZE_ID, k));
watch([dashboardPeriod, dashboardOrgs], load, {immediate: true});

async function load() {
    if (dashboardPeriod.value === '') {
        return;
    }
    const seq: number                    = ++loadSeq;
    loading.value                        = true;
    const result: ReeksenPage | undefined = await laadDashboard(() => dashboardReeksen(dashboardPeriod.value, dashboardOrgs.value, TOEGANKELIJKHEID_REEKSEN));
    if (seq !== loadSeq) {
        return;
    }
    page.value    = result ?? {series: [], zzc: [], notities: []};
    loading.value = false;
}
</script>

<template>
    <div class="px-10 py-4 flex flex-col gap-8">
        <section class="flex flex-col gap-2">
            <div class="text-xl font-semibold text-zzc-blue">{{ DashboardReeksEnum_name(DashboardReeksEnum.wachttijdenTreeknorm) }}</div>
            <div class="text-sm text-gray-500">{{ UITLEG }}</div>
            <div v-if="loading" class="text-sm text-gray-500">laden...</div>
            <div v-else-if="wachttijden.length === 0" class="text-sm text-gray-500">Geen gegevens voor deze selectie.</div>
            <div v-else-if="eenGroep" class="flex flex-col gap-2">
                <ReeksChart :serie="wachttijden[0]" type="bar"/>
                <div v-if="wachttijden[0].target" class="text-sm text-gray-500">Target: {{ wachttijden[0].target.omschrijving }}</div>
            </div>
            <div v-else class="grid grid-cols-3 gap-4">
                <div v-for="s in wachttijden" :key="s.organisatie" class="rounded border border-gray-200 bg-white p-2">
                    <div class="text-sm font-semibold">{{ s.organisatie }}</div>
                    <ReeksChart :serie="s" type="bar" compact/>
                </div>
            </div>
        </section>

        <section class="flex flex-col gap-2">
            <el-button-group>
                <el-button v-for="r in TOEGANKELIJKHEID_KEUZE"
                           :key="r"
                           :type="keuze === r ? 'primary' : 'default'"
                           @click="keuze = r"
                >
                    {{ DashboardReeksEnum_name(r) }}
                </el-button>
            </el-button-group>
            <div v-if="loading" class="text-sm text-gray-500">laden...</div>
            <div v-else-if="gekozen.length === 0" class="text-sm text-gray-500">Geen gegevens voor deze selectie.</div>
            <div v-else-if="gekozen.length === 1" class="flex flex-col gap-2">
                <div class="text-sm font-semibold">{{ gekozen[0].organisatie }}</div>
                <ReeksChart :serie="gekozen[0]" type="bar"/>
                <div v-if="gekozen[0].target" class="text-sm text-gray-500">Target: {{ gekozen[0].target.omschrijving }}</div>
            </div>
            <div v-else class="grid grid-cols-3 gap-4">
                <div v-for="s in gekozen" :key="s.organisatie" class="rounded border border-gray-200 bg-white p-2">
                    <div class="text-sm font-semibold">{{ s.organisatie }}</div>
                    <ReeksChart :serie="s" type="bar" compact/>
                </div>
            </div>
        </section>

        <ReeksNotities v-if="!eenGroep && !loading" :notities="notities" :reeksen="TOEGANKELIJKHEID_REEKSEN"/>
    </div>
</template>
